/**
 * Per-employee monthly attendance "muster card" (ESSL-style register).
 *
 * For a branch + month it produces, for every working employee, a per-day row with:
 *   status (P / ½P / A / WO / H / L / —), In/Out session times (up to 2 pairs),
 *   worked duration, late-by, early-by and OT, plus a month summary.
 *
 * Calculation basis (kept consistent with Daily hours / Payroll):
 *   - Punch instants are deduped (10s) and paired into in/out sessions.
 *   - Worked duration = sum of session time attributed inside the calendar day.
 *   - Shift window comes from the employee doc (ExpectedShiftStart/End,
 *     expectedStartTime, …) or the supplied branch default (e.g. 09:30–18:00).
 *   - Late by   = first punch after expected shift start.
 *   - Early by  = last punch before expected shift end.
 *   - OT        = worked duration beyond the expected shift length.
 *   - Sundays are Weekly Off (WO); `holidays` docs give H; per-employee
 *     `employee_cash_leave` docs give paid Leave (L).
 */
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { resolveCollectionName } from "../services/attendanceCollections";
import {
  mergeAndDedupeTimestamps,
  computeSessionsFromTimestamps,
  attributedMsInDay,
} from "./staffDailyHours";
import {
  parseClockToMinutes,
  expectedWindowFromDay,
  resolveExpectedShiftStrings,
} from "./dailyHoursPayroll";
import {
  ymd,
  isSunday,
  firstDefined,
  isWorkingEmployee,
  cutoffDateForSelectedMonth,
  fetchAttendanceForMonth,
  getAttendanceDocDayKeyAndDate,
  resolveAttendanceDocEmployeeIdCutover,
  buildEmployeeCodeLookupForShop,
  collectPunchTimesAllRoots,
  calendarDayKeyFromDate,
  buildHolidayYmdSetFromDocs,
  buildEmployeeCashLeaveYmdByEmployee,
  employeeCashLeaveYmdSet,
  workedHoursFromMergedTimesForDay,
  dayUnitFromWorkedHours,
  MULTI_PUNCH_FULL_DAY_MIN_COUNT,
} from "./payrollAttendanceCore";

const DEDUPE_SEC = 10;

/** ms → "H:MM" (can exceed 24h, e.g. monthly totals). */
export function fmtHM(ms) {
  const totalMin = Math.max(0, Math.round((Number(ms) || 0) / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

/** Date → "h:mm AM/PM" (12h, local). */
export function fmtClock(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

/** Date → compact 12h "h:mma" / "h:mmp" (fits narrow grid columns). */
export function fmtClockShort(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "p" : "a";
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")}${ap}`;
}

/** Calendar yyyy-mm-dd keys for every day of the month. */
function allDaysOfMonth(monthStart) {
  const out = [];
  const end = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  for (let d = new Date(monthStart); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(ymd(new Date(d)));
  }
  return out;
}

function dayBoundsFromKey(dayKey) {
  const [y, mo, da] = dayKey.split("-").map(Number);
  return {
    startOfDay: new Date(y, mo - 1, da, 0, 0, 0, 0),
    endOfDay: new Date(y, mo - 1, da + 1, 0, 0, 0, 0),
    dateObj: new Date(y, mo - 1, da),
  };
}

/**
 * @param {{ employeeCandidates?: string[], punchCandidates?: string[], label?: string }} currentShop
 * @param {Date} selectedMonth first day of month
 * @param {{ defaultShiftStart?: string, defaultShiftEnd?: string }} [shiftDefaults]
 * @returns {Promise<{ employees: object[], month: Date, monthDays: string[], todayKey: string, attendanceLoadMode: string }>}
 */
export async function loadMusterCardsForMonth(currentShop, selectedMonth, shiftDefaults = {}) {
  const defaults = {
    defaultShiftStart: shiftDefaults.defaultShiftStart || "09:30",
    defaultShiftEnd: shiftDefaults.defaultShiftEnd || "18:00",
  };
  const monthStart = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
  const monthEnd = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1);
  const monthDays = allDaysOfMonth(monthStart);

  const empCandidates = currentShop.employeeCandidates || [];
  const punchCandidates = currentShop.punchCandidates || [];

  const [employeeCollectionUsed, resolvedPunchCollection] = await Promise.all([
    resolveCollectionName(empCandidates),
    resolveCollectionName(punchCandidates),
  ]);
  if (!employeeCollectionUsed) throw new Error("Employees collection not found");
  const punchName = resolvedPunchCollection || punchCandidates[0];
  if (!punchName) throw new Error("Attendance collection not found");

  const cutoffDt = cutoffDateForSelectedMonth(selectedMonth);

  const [holidaySnap, empSnap, attPack] = await Promise.all([
    getDocs(collection(db, "holidays")).catch(() => ({ docs: [] })),
    getDocs(collection(db, employeeCollectionUsed)),
    fetchAttendanceForMonth(punchName, monthStart, monthEnd),
  ]);

  const holidayYmdSet = buildHolidayYmdSetFromDocs(holidaySnap.docs, monthStart, monthEnd);
  const cashLeaveByEmp = buildEmployeeCashLeaveYmdByEmployee(holidaySnap.docs, monthStart, monthEnd);

  /** canonical employeeId -> meta */
  const employeeMap = new Map();
  /** raw key -> canonical employeeId */
  const employeeLookup = new Map();
  const norm = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  for (const docSnap of empSnap.docs) {
    const d = docSnap.data() || {};
    if (!isWorkingEmployee(d)) continue;
    const employeeId = String(
      firstDefined(d, [
        "employeeId",
        "empId",
        "uid",
        "user_id",
        "userId",
        "code",
        "employeeCode",
        "EmployeeId",
      ]) || docSnap.id
    );
    const employeeName = String(
      firstDefined(d, ["name", "employeeName", "fullName", "empName", "username", "Name", "EmployeeName"]) ||
        employeeId
    );
    const shift = resolveExpectedShiftStrings(employeeId, { raw: d }, defaults, {});
    if (!employeeMap.has(employeeId)) {
      employeeMap.set(employeeId, {
        employeeId,
        employeeName,
        department: String(firstDefined(d, ["department", "dept", "Department"]) || ""),
        designation: String(firstDefined(d, ["designation", "Designation", "role", "title"]) || ""),
        shiftStartStr: shift.startStr,
        shiftEndStr: shift.endStr,
        shiftStartMin: shift.startMin,
        shiftEndMin: shift.endMin,
        /** dayKey -> { times: Date[] } */
        punchesByDay: new Map(),
      });
    }
    const keyCandidates = [
      employeeId,
      firstDefined(d, [
        "employeeId",
        "empId",
        "uid",
        "user_id",
        "userId",
        "code",
        "employeeCode",
        "EmployeeId",
      ]),
      firstDefined(d, ["email", "mail"]),
      firstDefined(d, ["username", "usernameLower"]),
      firstDefined(d, ["name", "employeeName", "fullName", "empName", "Name", "EmployeeName"]),
    ].filter(Boolean);
    for (const key of keyCandidates) {
      const raw = String(key).trim().toLowerCase();
      const n = norm(key);
      if (raw && !employeeLookup.has(raw)) employeeLookup.set(raw, employeeId);
      if (n && !employeeLookup.has(n)) employeeLookup.set(n, employeeId);
    }
  }

  const employeeCodeLookup = buildEmployeeCodeLookupForShop(currentShop, empSnap.docs);

  for (const docSnap of attPack.snap.docs) {
    const d = docSnap.data() || {};
    const ctx = getAttendanceDocDayKeyAndDate(d, docSnap.id);
    if (!ctx) continue;
    const { dateObj } = ctx;
    if (!dateObj || dateObj < monthStart || dateObj >= monthEnd) continue;
    const resolvedId = resolveAttendanceDocEmployeeIdCutover(d, docSnap.id, dateObj, {
      employeeLookup,
      employeeCodeLookup,
    });
    if (!resolvedId || !employeeMap.has(resolvedId)) continue;
    const calKey = calendarDayKeyFromDate(dateObj);
    if (!calKey) continue;
    const emp = employeeMap.get(resolvedId);
    if (!emp.punchesByDay.has(calKey)) emp.punchesByDay.set(calKey, []);
    const times = collectPunchTimesAllRoots(d);
    for (const t of times) emp.punchesByDay.get(calKey).push(t);
  }

  const todayKey = ymd(cutoffDt);

  const employees = Array.from(employeeMap.values()).map((emp) => {
    const cashLeaveSet = employeeCashLeaveYmdSet(cashLeaveByEmp, emp.employeeId);
    const days = [];

    const summary = {
      presentUnits: 0,
      absentDays: 0,
      weeklyOff: 0,
      holidays: 0,
      leaves: 0,
      workMs: 0,
      otMs: 0,
      lateMs: 0,
      lateDays: 0,
      earlyMs: 0,
      earlyDays: 0,
      workedDays: 0,
    };

    for (const dayKey of monthDays) {
      const { startOfDay, endOfDay, dateObj } = dayBoundsFromKey(dayKey);
      const isFuture = dayKey > todayKey;
      const sunday = isSunday(dateObj);
      const isHoliday = holidayYmdSet.has(dayKey);
      const isLeave = cashLeaveSet.has(dayKey);

      const rawTimes = emp.punchesByDay.get(dayKey) || [];
      const merged = mergeAndDedupeTimestamps(rawTimes, DEDUPE_SEC);
      const { sessions } = computeSessionsFromTimestamps(merged);
      const attributedMs = attributedMsInDay(sessions, startOfDay, endOfDay);

      const inOut = [];
      for (let i = 0; i + 1 < merged.length; i += 2) {
        inOut.push({ in: merged[i], out: merged[i + 1] });
      }
      const danglingIn = merged.length % 2 === 1 ? merged[merged.length - 1] : null;

      let lateMs = 0;
      let earlyMs = 0;
      let otMs = 0;
      let shiftMs = 0;
      const win = expectedWindowFromDay(startOfDay, emp.shiftStartMin, emp.shiftEndMin);
      if (win) {
        shiftMs = Math.max(0, win.expEnd.getTime() - win.expStart.getTime());
        if (merged.length) {
          const first = merged[0];
          const last = merged[merged.length - 1];
          if (first.getTime() > win.expStart.getTime()) lateMs = first.getTime() - win.expStart.getTime();
          if (last.getTime() < win.expEnd.getTime()) earlyMs = win.expEnd.getTime() - last.getTime();
        }
      }
      if (shiftMs > 0 && attributedMs > shiftMs) otMs = attributedMs - shiftMs;

      let dayUnit = 0;
      if (merged.length >= MULTI_PUNCH_FULL_DAY_MIN_COUNT) {
        dayUnit = 1;
      } else if (attributedMs > 0) {
        dayUnit = dayUnitFromWorkedHours(workedHoursFromMergedTimesForDay(merged, dayKey));
      } else if (merged.length >= 1) {
        dayUnit = merged.length >= 2 ? 1 : 0;
      }

      let status;
      if (merged.length > 0) {
        status = dayUnit >= 0.999 ? "P" : dayUnit > 0 ? "½P" : "P";
      } else if (sunday) {
        status = "WO";
      } else if (isHoliday) {
        status = "H";
      } else if (isLeave) {
        status = "L";
      } else if (isFuture) {
        status = "";
      } else {
        status = "A";
      }

      if (status === "P" || status === "½P") {
        summary.presentUnits += status === "½P" ? 0.5 : 1;
        summary.workMs += attributedMs;
        summary.otMs += otMs;
        if (attributedMs > 0) summary.workedDays += 1;
        if (lateMs > 60000) {
          summary.lateMs += lateMs;
          summary.lateDays += 1;
        }
        if (earlyMs > 60000) {
          summary.earlyMs += earlyMs;
          summary.earlyDays += 1;
        }
      } else if (status === "WO") {
        summary.weeklyOff += 1;
        // OT can still be earned on a weekly-off if the employee worked (no punches => none).
      } else if (status === "H") {
        summary.holidays += 1;
      } else if (status === "L") {
        summary.leaves += 1;
      } else if (status === "A") {
        summary.absentDays += 1;
      }

      days.push({
        dayKey,
        date: dateObj,
        dayNum: dateObj.getDate(),
        weekday: dateObj.toLocaleDateString("en-IN", { weekday: "short" }),
        status,
        in1: inOut[0]?.in || danglingIn || null,
        out1: inOut[0]?.out || null,
        in2: inOut[1]?.in || (inOut.length === 1 ? danglingIn : null) || null,
        out2: inOut[1]?.out || null,
        durationMs: attributedMs,
        lateMs,
        earlyMs,
        otMs,
        shiftStr: emp.shiftStartStr ? emp.shiftStartStr.replace(/^0/, "") : "",
        punchCount: merged.length,
      });
    }

    const totalDurationWithOtMs = summary.workMs + summary.otMs;
    const avgWorkMs = summary.workedDays > 0 ? Math.round(summary.workMs / summary.workedDays) : 0;

    return {
      ...emp,
      punchesByDay: undefined,
      days,
      summary: { ...summary, totalDurationWithOtMs, avgWorkMs },
    };
  });

  employees.sort((a, b) => a.employeeName.localeCompare(b.employeeName));

  return {
    employees,
    month: monthStart,
    monthDays,
    todayKey,
    attendanceLoadMode: attPack.mode,
    shiftDefaults: defaults,
  };
}

/** Validate "HH:mm"; falls back to provided default. */
export function safeClock(str, fallback) {
  return parseClockToMinutes(str) != null ? str : fallback;
}
